import {
  User, Star, Calendar, MessageSquare, CheckCircle 
} from 'lucide-react';
import type { Feedback } from '../../types';
import {
  getFeedbackTypeColor, getFeedbackStatusColor 
} from '../../types';

interface FeedbackCardProps {
  readonly feedback: Feedback;
  readonly onClick?: () => void;
}

const STAR_POSITIONS = ['first', 'second', 'third', 'fourth', 'fifth'] as const;

export function FeedbackCard({
  feedback, onClick 
}: FeedbackCardProps) {
  const typeColor = getFeedbackTypeColor(feedback.type);
  const statusColor = getFeedbackStatusColor(feedback.status);
  const formattedDate = new Date(feedback.createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-lg border bg-white p-4 text-left transition-shadow hover:shadow-md"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-primary/10 p-2">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{feedback.employeeName}</h3>
            <p className="text-sm text-gray-500">{feedback.employeeRole}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${typeColor}`}>
            {feedback.type}
          </span>
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusColor}`}>
            {feedback.status}
          </span>
        </div>
      </div>

      <div className="mt-3">
        <h4 className="font-medium text-gray-800">{feedback.title}</h4>
        <p className="mt-1 line-clamp-2 text-sm text-gray-600">{feedback.content}</p>
      </div>

      {feedback.rating !== undefined && (
        <div className="mt-3 flex items-center gap-1">
          {STAR_POSITIONS.map((position, starIndex) => (
            <Star
              key={`star-${feedback.feedbackId}-${position}`}
              className={`h-4 w-4 ${
                starIndex < (feedback.rating ?? 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
              }`}
            />
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <MessageSquare className="h-3 w-3" />
            {feedback.category}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {formattedDate}
          </span>
        </div>
        {feedback.acknowledgedAt && (
          <span className="flex items-center gap-1 text-green-600">
            <CheckCircle className="h-3 w-3" />
            Acknowledged
          </span>
        )}
      </div>

      <div className="mt-2 text-xs text-gray-400">
        Submitted by {feedback.submittedByName} • {feedback.siteName}
      </div>
    </button>
  );
}
